import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { FieldRenderer } from "@/components/tuw/post/FieldRenderer"
import { outlineChoice, paidOutline } from "@/lib/openHouse/bookWritingSteps"
import { WORDING } from "@/lib/openHouse/wording"
import { ErrorLine, UploadControl } from "./UploadControl"

// The Outline step (phase-1-wording §1): add the paid outline at its live
// price (today's add_outline), or supply my own (Open House supplies_outline),
// uploaded as a file or pasted. Everything, the file included, saves through
// the page's draft save queue. Upload or paste is the saved outline_source
// (onSource saves it at once); only that source is shown and supplied.
//
// upload: { status: "idle" | "uploading" | "done" | "failed" | "unsupported" | "tooLarge", file }

const OUTLINE_WORDS = {
  uploading: WORDING.uploading, uploaded: WORDING.uploaded, failed: WORDING.uploadFailed, retry: WORDING.retryUpload,
  empty: WORDING.uploadEmpty, button: WORDING.fileButton, unsupported: WORDING.unsupported,
  tooLarge: WORDING.uploadTooLarge,
}

// paidOption replaces the paid outline (a Silver or Gold bundle's Platinum upgrade).
// On the guest page (ctx.guest) the fee is only ever the quote's (ctx.quotedOutline).
export function OutlineChoice({
  values, ctx, oh, error, setField, paidOption, upload, onSource, onFile, onRetry, onRemove,
}) {
  const mode = oh.outline_source
  const choice = outlineChoice(values, oh)
  const paid = paidOutline(values, ctx)

  const choiceField = {
    type: "radio",
    key: "outline_choice",
    required: true,
    options: [
      paidOption || {
        value: "paid", label: values.fiction ? WORDING.paidFiction : WORDING.paidNonfiction, price: ctx?.guest ? ctx.quotedOutline : paid?.price, sub: WORDING.paidHelp,
      },
      { value: "own", label: WORDING.supplied, sub: WORDING.suppliedHelp },
    ],
  }
  const pasteField = {
    type: "textarea", key: "outline_text", required: true, rows: 8,
    label: WORDING.pasteLabel, placeholder: WORDING.pastePlaceholder,
  }

  return (
    <div className="flex flex-col gap-4">
      <FieldRenderer
        field={choiceField}
        values={{ outline_choice: choice }}
        setField={setField}
        error={error === WORDING.choiceError ? error : null}
        ctx={ctx}
      />

      {choice === "own" && (
        <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-accent px-3.5 py-3.5">
          <div className="text-[12.5px] font-bold text-foreground">{WORDING.supplyLabel}</div>
          <ToggleGroup
            type="single"
            value={mode || ""}
            onValueChange={(next) => next && onSource(next)}
            aria-label={WORDING.supplyLabel}
          >
            <ToggleGroupItem value="upload">{WORDING.modeUpload}</ToggleGroupItem>
            <ToggleGroupItem value="paste">{WORDING.modePaste}</ToggleGroupItem>
          </ToggleGroup>

          {mode === "upload" && (
            <>
              <UploadControl
                saved={oh.outlineFile?.filename}
                upload={upload}
                accept=".doc,.docx,.pdf,.zip"
                words={OUTLINE_WORDS}
                onFile={onFile}
                onRetry={onRetry}
                onRemove={onRemove}
              />
              <p className="text-[12px] leading-snug text-muted-foreground">{WORDING.uploadHelp}</p>
            </>
          )}
          {mode === "paste" && (
            <FieldRenderer field={pasteField} values={{ outline_text: oh.outline_text }} setField={setField} ctx={ctx} />
          )}

          {error && error !== WORDING.choiceError && error !== WORDING.uploading && <ErrorLine>{error}</ErrorLine>}
        </div>
      )}
    </div>
  )
}
